import React from "react";
import api from "@services/api";
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import "./ask-ai.css";

const COLORS = ["#2563eb", "#16a34a", "#f59e0b", "#db2777", "#7c3aed", "#0891b2", "#dc2626", "#65a30d"];
const STAGES = ["Understanding your question…", "Writing a database query…", "Fetching the data…", "Preparing your answer…"];

function numericColumns(columns, rows) {
  if (!rows?.length) return [];
  return columns.filter((c) => rows.every((r) => r[c] === null || r[c] === "" || !isNaN(Number(r[c]))));
}

function toCsv(columns, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columns.join(","), ...rows.map((r) => columns.map((c) => esc(r[c])).join(","))].join("\n");
}

function Chart({ chart, columns, rows }) {
  const type = chart?.type || "none";
  if (type === "none" || !rows?.length) return null;

  const x = chart?.x && columns.includes(chart.x) ? chart.x : columns[0];
  let ys = (chart?.y || []).filter((y) => columns.includes(y) && y !== x);
  if (!ys.length) ys = numericColumns(columns, rows).filter((c) => c !== x).slice(0, 3);
  if (!ys.length) return null;

  const data = rows.map((r) => {
    const o = { ...r };
    ys.forEach((y) => (o[y] = Number(o[y])));
    return o;
  });

  if (type === "pie") {
    const y = ys[0];
    return (
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie data={data} dataKey={y} nameKey={x} cx="50%" cy="50%" outerRadius={110} label>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip /><Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  const Comp = type === "line" ? LineChart : BarChart;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <Comp data={data} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis dataKey={x} tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip /><Legend />
        {ys.map((y, i) =>
          type === "line"
            ? <Line key={y} type="monotone" dataKey={y} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
            : <Bar key={y} dataKey={y} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />
        )}
      </Comp>
    </ResponsiveContainer>
  );
}

function DataTable({ columns, rows }) {
  if (!rows?.length) return <div className="ai-muted">No rows.</div>;
  return (
    <div className="ai-table-wrap">
      <table className="ai-table">
        <thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{columns.map((c) => <td key={c}>{r[c] == null ? "" : String(r[c])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultCard({ msg }) {
  const [view, setView] = React.useState(null); // null=auto, 'table', 'chart'
  const [showSql, setShowSql] = React.useState(false);
  const { title, answer, sql, chart, columns = [], rows = [], row_count, error } = msg.result || {};

  if (msg.status === "error" || error) {
    return <div className="ai-card ai-error">⚠️ {error || "Something went wrong. Try rephrasing your question."}</div>;
  }

  const hasChart = chart && chart.type && chart.type !== "none" && rows.length > 0;
  const effView = view || (hasChart ? "chart" : "table");

  const download = () => {
    const blob = new Blob([toCsv(columns, rows)], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (title || "result").replace(/[^\w]+/g, "_").toLowerCase() + ".csv";
    a.click();
  };

  return (
    <div className="ai-card">
      {title && <div className="ai-card-title">{title}</div>}
      {answer && <div className="ai-answer">{answer}</div>}

      {rows.length > 0 && (
        <>
          <div className="ai-toolbar">
            {hasChart && (
              <div className="ai-toggle">
                <button className={effView === "chart" ? "on" : ""} onClick={() => setView("chart")}>Chart</button>
                <button className={effView === "table" ? "on" : ""} onClick={() => setView("table")}>Table</button>
              </div>
            )}
            <span className="ai-muted">{row_count} row{row_count === 1 ? "" : "s"}</span>
            <div className="ai-spacer" />
            <button className="ai-linkbtn" onClick={download}>⬇ CSV</button>
            <button className="ai-linkbtn" onClick={() => setShowSql((s) => !s)}>{showSql ? "Hide" : "Show"} SQL</button>
          </div>

          {effView === "chart" ? <Chart chart={chart} columns={columns} rows={rows} /> : <DataTable columns={columns} rows={rows} />}

          {showSql && <pre className="ai-sql">{sql}</pre>}
        </>
      )}
    </div>
  );
}

export default function AskAiPage() {
  const [messages, setMessages] = React.useState([]); // {role, text} | {role:'ai', status, result}
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [stage, setStage] = React.useState(0);
  const [suggestions, setSuggestions] = React.useState([]);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    api.get("/ai-insights/suggestions").then((r) => setSuggestions(r.data?.data || [])).catch(() => {});
  }, []);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy, stage]);

  const ask = async (question) => {
    const q = (question ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setStage(0);
    setMessages((m) => [...m, { role: "user", text: q }]);

    const stageTimer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 6000);
    try {
      const { data } = await api.post("/ai-insights/ask", { question: q });
      const jobId = data.job_id;
      // Poll for the result.
      let result = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const r = await api.get(`/ai-insights/result/${jobId}`);
        if (r.data.status === "done" || r.data.status === "error") { result = r.data; break; }
      }
      clearInterval(stageTimer);
      if (!result) {
        setMessages((m) => [...m, { role: "ai", status: "error", result: { error: "Timed out. Please try again." } }]);
      } else {
        setMessages((m) => [...m, { role: "ai", status: result.status, result }]);
      }
    } catch (e) {
      clearInterval(stageTimer);
      const msg = e?.response?.data?.message || "Request failed. Please try again.";
      setMessages((m) => [...m, { role: "ai", status: "error", result: { error: msg } }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ai-page">
      <div className="ai-head">
        <h2>✨ Ask AI</h2>
        <p className="ai-muted">Ask about your orders, revenue, customers or products in plain English. The AI reads the database (read-only) and answers with a chart or table.</p>
      </div>

      <div className="ai-chat" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="ai-empty">
            <div className="ai-muted" style={{ marginBottom: 10 }}>Try one of these:</div>
            <div className="ai-chips">
              {suggestions.map((s) => (
                <button key={s} className="ai-chip" onClick={() => ask(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === "user"
            ? <div key={i} className="ai-bubble user">{m.text}</div>
            : <ResultCard key={i} msg={m} />
        )}

        {busy && (
          <div className="ai-card ai-loading">
            <span className="ai-spinner" /> {STAGES[stage]}
          </div>
        )}
      </div>

      <form className="ai-inputbar" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input
          className="ai-input"
          placeholder="e.g. How much revenue did we make last month?"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
        />
        <button className="ai-send" type="submit" disabled={busy || !input.trim()}>
          {busy ? "Working…" : "Ask"}
        </button>
      </form>
    </div>
  );
}
